import { chain } from '@leprecoins/effector';
import { attach } from 'effector';
import { AccountReportRegistryModel } from 'entities/account';
import {
	$t,
	createExportReportModel,
	EXPORT_FORMAT,
	ExportFormat,
	ExportReportModel,
} from 'shared/lib';

export type ExportReportAccountsModel = ExportReportModel;

export const createExportReportAccountsModel = (
	model: AccountReportRegistryModel,
): ExportReportModel => {
	const exportReportModel = createExportReportModel();

	const exportFx = attach({
		source: [
			model.model.$rows,
			model.$total,
			model.$isMultiCurrencies,
			$t,
			model.dateFilterModel.dateFilter.$value,
		] as const,
		effect: ([rows, total, isMultiCurrencies, t, dateFilter], format: ExportFormat) => {
			const headers = [
				t('account-report-registry.table-columns.account'),
				t('account-report-registry.table-columns.currency'),
				t('account-report-registry.table-columns.deposits'),
				t('account-report-registry.table-columns.withdrawals'),
				t('account-report-registry.table-columns.jackpots'),
				t('account-report-registry.table-columns.bonuses'),
				t('account-report-registry.table-columns.income'),
				t('account-report-registry.table-columns.income-all-sources-tooltip'),
				t('account-report-registry.table-columns.rtp'),
				t('account-report-registry.table-columns.rtp-all-sources-tooltip'),
				t('account-report-registry.table-columns.number-of-bets'),
			];

			const body = rows.map((row) => [
				typeof row.account === 'string' ? row.account : (row.account?.name ?? ''),
				row.currency,
				row.deposits,
				row.withdrawals,
				row.jackpots,
				row.bonuses,
				row.income,
				row.incomeAllSources,
				`${(row.rtp * 100).toFixed(2)} %`,
				`${(row.rtpAllSources * 100).toFixed(2)} %`,
				row.numberOfBets,
			]);

			const footer = [
				t('account-report-registry.table-footer.total'),
				total?.currency || '',
				isMultiCurrencies ? '' : total?.deposits || 0,
				isMultiCurrencies ? '' : total?.withdrawals || 0,
				isMultiCurrencies ? '' : total?.jackpots || 0,
				isMultiCurrencies ? '' : total?.bonuses || 0,
				isMultiCurrencies ? '' : total?.income || 0,
				isMultiCurrencies ? '' : total?.incomeAllSources || 0,
				`${total?.rtp ?? 0} %`,
				`${total?.rtpAllSources ?? 0} %`,
				total?.numberOfBets || 0,
			];

			if (format === EXPORT_FORMAT.pdf) {
				exportReportModel.saveAsPdfFx({
					head: [headers],
					body: [...body],
					footer: [footer],
					dates: dateFilter,
					filenameTemplate: t('accounts-report%DATE%.pdf'),
					documentOptions: {
						orientation: 'landscape',
					},
				});

				return;
			}

			exportReportModel.saveAsExcelFx({
				data: [headers, ...body, footer],
				dates: dateFilter,
				filenameTemplate: 'accounts-report%DATE%.xlsx',
				sheetTitle: 'Accounts Report',
				columnWidths: [
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					25,
					undefined,
					25,
					undefined,
				],
				enableBorders: true,
			});
		},
	});

	chain(exportReportModel.exportButtonClicked, exportFx);

	return exportReportModel;
};
